(() => {
  const SELECTOR = 'fortune-tail-newsletter-popup';
  const CART_DRAWER_SELECTOR = 'fortune-tail-cart-drawer';
  const ENGAGEMENT_DELAY = 5000;
  const DISMISSAL_DAYS = 14;
  const DISMISSED_UNTIL_KEY = 'fortuneTailNewsletterDismissedUntil';
  const SUBSCRIBED_KEY = 'fortuneTailNewsletterSubscribed';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class FortuneTailNewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButton = this.querySelector('.ft-newsletter-popup__close');
      this.formStatus = this.dataset.formStatus || 'idle';
      this.previouslyFocused = null;
      this.automaticTriggerConsumed = false;
      this.pendingCartClose = false;
      this.engagementTimer = null;
      this.cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);
      if (this.cartDrawer) {
        this.cartObserver = new MutationObserver(this.handleCartStateChange);
        this.cartObserver.observe(this.cartDrawer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
      }
      if (this.formStatus === 'success') {
        window.localStorage.setItem(SUBSCRIBED_KEY, 'true');
        this.show();
      } else if (this.formStatus === 'error') {
        this.show();
      } else if (this.canOpenAutomatically()) {
        this.engagementTimer = window.setTimeout(() => this.requestOpen(true), ENGAGEMENT_DELAY);
      }
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
      window.clearTimeout(this.engagementTimer);
      this.cartObserver?.disconnect();
      if (!this.hidden) document.body.classList.remove('ft-newsletter-popup-open');
      this.initialized = false;
    }
    canOpenAutomatically() {
      if (window.localStorage.getItem(SUBSCRIBED_KEY) === 'true') return false;
      const dismissedUntil = Number(window.localStorage.getItem(DISMISSED_UNTIL_KEY));
      return !dismissedUntil || Date.now() >= dismissedUntil;
    }
    handleCartStateChange = () => {
      if (!this.pendingCartClose || this.isCartDrawerOpen()) return;
      this.pendingCartClose = false;
      this.show();
    };
    isCartDrawerOpen() {
      return Boolean(this.cartDrawer?.classList.contains('is-open') || this.cartDrawer?.getAttribute('aria-hidden') === 'false');
    }
    requestOpen(isAutomatic = false) {
      if (isAutomatic && this.automaticTriggerConsumed) return;
      this.automaticTriggerConsumed = true;
      if (this.isCartDrawerOpen()) {
        this.pendingCartClose = true;
        return;
      }
      this.show();
    }
    handleClick = (event) => {
      if (event.target.closest('[data-newsletter-close]')) this.close();
    };
    handleKeydown = (event) => {
      if (this.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.querySelectorAll(FOCUSABLE)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    open() {
      this.requestOpen(false);
    }
    show() {
      if (!this.hidden) return;
      this.previouslyFocused = document.activeElement;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ft-newsletter-popup-open');
      requestAnimationFrame(() => this.closeButton?.focus());
    }
    close() {
      if (this.hidden) return;
      if (this.formStatus !== 'success') {
        const dismissedUntil = Date.now() + DISMISSAL_DAYS * 24 * 60 * 60 * 1000;
        window.localStorage.setItem(DISMISSED_UNTIL_KEY, String(dismissedUntil));
      }
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ft-newsletter-popup-open');
      if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
      this.previouslyFocused = null;
    }
  }
  if (!customElements.get(SELECTOR)) customElements.define(SELECTOR, FortuneTailNewsletterPopup);
  const getPopup = () => document.querySelector(SELECTOR);
  window.FortuneTailNewsletter = { open: () => getPopup()?.open(), close: () => getPopup()?.close() };
})();
